import { useState, useEffect, useCallback } from 'react'
import { useChatStore } from '../../stores/chatStore'
import { useUiStore } from '../../stores/uiStore'
import { searchApi } from '../../api/search'
import type { MessageHit } from '../../api/search'
import type { Message } from '../../types'

interface Props {
  chatId: string
  onClose: () => void
}

type Filter = 'all' | 'media' | 'files' | 'links'

const URL_RE = /https?:\/\/[^\s]+/

function matchesFilter(m: Message, filter: Filter): boolean {
  if (filter === 'all') return true
  if (filter === 'media') return !!(m.media_ids?.length)
  if (filter === 'files') return !!(m.media_ids?.length)
  if (filter === 'links') return URL_RE.test(m.text ?? '')
  return true
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

export function ChatSearchBar({ chatId, onClose }: Props) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [jumpDate, setJumpDate] = useState('')
  const [showJump, setShowJump] = useState(false)
  const [serverResults, setServerResults] = useState<(Message | MessageHit)[]>([])
  const [searching, setSearching] = useState(false)
  const messages = useChatStore((s) => s.messages[chatId] ?? [])
  const setHighlightedMessage = useUiStore((s) => s.setHighlightedMessage)
  const debouncedQuery = useDebounce(query, 350)

  // Server search for text queries; local filter for type filters
  const fetchServerResults = useCallback(async (q: string) => {
    if (q.length < 2) { setServerResults([]); return }
    setSearching(true)
    try {
      // searchApi.inChat now calls messaging service directly (no ES needed)
      const hits = await searchApi.inChat(chatId, q)
      setServerResults((hits ?? []) as Message[])
    } catch {
      setServerResults([])
    } finally {
      setSearching(false)
    }
  }, [chatId])

  useEffect(() => {
    if (filter === 'all') {
      fetchServerResults(debouncedQuery)
    } else {
      setServerResults([])
    }
  }, [debouncedQuery, filter, fetchServerResults])

  const results: (Message | MessageHit)[] = (() => {
    if (filter !== 'all') {
      return messages.filter((m) => matchesFilter(m, filter))
    }
    if (query.length < 2) return []
    // Merge server results with locally cached, dedup by id
    const serverIds = new Set(serverResults.map((m) => m.id))
    const localHits = messages.filter((m) =>
      !serverIds.has(m.id) && m.text?.toLowerCase().includes(query.toLowerCase())
    )
    return [...serverResults, ...localHits]
  })()

  const [idx, setIdx] = useState(0)

  useEffect(() => {
    if (results.length > 0) {
      setHighlightedMessage(results[idx]?.id ?? null)
    } else {
      setHighlightedMessage(null)
    }
  }, [idx, results, setHighlightedMessage])

  useEffect(() => {
    return () => setHighlightedMessage(null)
  }, [setHighlightedMessage])

  useEffect(() => { setIdx(0) }, [query, filter])

  const navigate = (dir: -1 | 1) => {
    if (!results.length) return
    setIdx((prev) => {
      const next = prev + dir
      if (next < 0) return results.length - 1
      if (next >= results.length) return 0
      return next
    })
  }

  const handleJumpToDate = () => {
    if (!jumpDate) return
    const target = new Date(jumpDate).getTime()
    const closest = messages.reduce<Message | null>((acc, m) => {
      if (!acc) return m
      const aDiff = Math.abs(new Date(acc.created_at).getTime() - target)
      const bDiff = Math.abs(new Date(m.created_at).getTime() - target)
      return bDiff < aDiff ? m : acc
    }, null)
    if (closest) {
      setHighlightedMessage(closest.id)
    }
    setShowJump(false)
  }

  const FILTERS: { key: Filter; label: string }[] = [
    { key: 'all', label: 'Все' },
    { key: 'media', label: '🖼 Медиа' },
    { key: 'files', label: '📎 Файлы' },
    { key: 'links', label: '🔗 Ссылки' },
  ]

  return (
    <div className="bg-bg-primary border-b border-divider shrink-0">
      <div className="flex items-center gap-2 px-4 py-2">
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск в чате..."
          className="flex-1 bg-bg-input rounded-xl px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-accent text-txt-primary placeholder-txt-meta"
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose()
            if (e.key === 'Enter') { e.shiftKey ? navigate(-1) : navigate(1) }
            if (e.key === 'ArrowUp') { e.preventDefault(); navigate(-1) }
            if (e.key === 'ArrowDown') { e.preventDefault(); navigate(1) }
          }}
        />
        {searching && (
          <div className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin shrink-0" />
        )}
        {results.length > 0 && !searching && (
          <span className="text-txt-meta text-xs shrink-0">{idx + 1}/{results.length}</span>
        )}
        {!searching && query.length >= 2 && filter === 'all' && results.length === 0 && (
          <span className="text-txt-meta text-xs shrink-0">Не найдено</span>
        )}
        <button onClick={() => navigate(-1)} disabled={results.length === 0}
          className="text-txt-secondary hover:text-txt-primary text-sm px-1 disabled:opacity-40">↑</button>
        <button onClick={() => navigate(1)} disabled={results.length === 0}
          className="text-txt-secondary hover:text-txt-primary text-sm px-1 disabled:opacity-40">↓</button>
        {/* Jump to date */}
        <div className="relative">
          <button
            onClick={() => setShowJump((s) => !s)}
            className="text-txt-secondary hover:text-txt-primary text-xs px-1.5 py-1 rounded-lg hover:bg-bg-hover transition-colors"
            title="Перейти к дате"
          >
            📅
          </button>
          {showJump && (
            <div className="absolute top-full right-0 mt-1 bg-bg-primary border border-divider rounded-xl shadow-xl z-30 p-2 flex gap-1 min-w-max">
              <input
                type="date"
                value={jumpDate}
                onChange={(e) => setJumpDate(e.target.value)}
                className="bg-bg-input text-txt-primary text-sm rounded-lg px-2 py-1 outline-none focus:ring-1 focus:ring-accent"
              />
              <button
                onClick={handleJumpToDate}
                disabled={!jumpDate}
                className="bg-accent text-white text-sm px-2 rounded-lg disabled:opacity-40 hover:bg-blue-500 transition-colors"
              >
                Перейти
              </button>
            </div>
          )}
        </div>
        <button onClick={onClose} className="text-txt-meta hover:text-txt-primary text-sm px-1" aria-label="Close">✕</button>
      </div>

      {/* Filter chips */}
      <div className="flex gap-1 px-4 pb-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-2.5 py-0.5 text-xs rounded-full transition-colors font-medium
              ${filter === f.key ? 'bg-accent text-white' : 'bg-bg-input text-txt-secondary hover:text-txt-primary hover:bg-bg-hover'}`}
          >
            {f.label}
          </button>
        ))}
      </div>
    </div>
  )
}
